import { useCallback, useEffect, useState } from 'react'
import { api, errorText } from '../api/client'
import type { CalendarStatus } from '../api/types'
import { Icon } from './Icon'
import { useConfirm } from './Confirm'
import { useToast } from './Toast'

/** What the OAuth callback redirects back with, in words. The server can only
 *  put a short code in the URL, so the sentence lives here. */
const OUTCOMES: Record<string, { text: string; tone: 'positive' | 'warning' }> = {
  connected: { text: 'Your Google Calendar is connected.', tone: 'positive' },
  denied: { text: 'Google access was declined, so nothing was connected.', tone: 'warning' },
  expired: { text: 'That took too long — start the connection again.', tone: 'warning' },
  failed: { text: 'Google couldn’t complete the connection. Try again.', tone: 'warning' },
}

function when(iso: string | null): string {
  if (!iso) return 'not yet'
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const hours = Math.round(mins / 60)
  return hours < 24 ? `${hours}h ago` : new Date(iso).toLocaleDateString()
}

/** Connecting a Google Calendar, and saying plainly what that will do.
 *
 *  The card is deliberately explicit about the two directions, because "two-way
 *  sync" is the kind of phrase people read past — an officer should know their
 *  existing Google events will appear in the CRM before they click, not after. */
export function GoogleCalendarCard({ outcome, onOutcomeSeen }: {
  outcome?: string | null
  onOutcomeSeen?: () => void
}) {
  const [status, setStatus] = useState<CalendarStatus | null>(null)
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  const confirm = useConfirm()

  const load = useCallback(() => {
    api.googleStatus().then(setStatus).catch(() => setStatus(null))
  }, [])

  useEffect(load, [load])

  // The redirect back from Google carries the result; say it once, then clear it
  // so a refresh doesn't repeat a stale message.
  useEffect(() => {
    if (!outcome) return
    const result = OUTCOMES[outcome]
    if (result) toast(result.text, { tone: result.tone })
    onOutcomeSeen?.()
  }, [outcome, toast, onOutcomeSeen])

  if (!status) return null

  if (!status.configured) {
    return (
      <section className="card">
        <div className="card-head"><h2 className="h2">Google Calendar</h2></div>
        <div className="card-body" style={{ paddingTop: 0 }}>
          <p className="quiet" style={{ fontSize: 12.5 }}>
            Calendar sync isn’t set up on this server yet. It needs a Google OAuth client —
            once the client id and secret are in the environment, this card turns into a
            Connect button.
          </p>
        </div>
      </section>
    )
  }

  const connect = async () => {
    setBusy(true)
    try {
      const { url } = await api.googleConnect()
      window.location.href = url   // leaving the app for Google's consent screen
    } catch (e) {
      toast(errorText(e), { tone: 'warning' })
      setBusy(false)
    }
  }

  const disconnect = async () => {
    if (!(await confirm({
      title: 'Disconnect Google Calendar?',
      body: 'Your appointments stay in the CRM. They stop syncing, and the copies already on '
        + 'your Google Calendar are left where they are.',
      confirmLabel: 'Disconnect',
      danger: true,
    }))) return
    setBusy(true)
    try {
      setStatus(await api.googleDisconnect())
      toast('Google Calendar disconnected.')
    } catch (e) {
      toast(errorText(e), { tone: 'warning' })
    } finally {
      setBusy(false)
    }
  }

  const syncNow = async () => {
    setBusy(true)
    try {
      const result = await api.googleSync()
      setStatus(result)
      toast(result.applied ? `Brought in ${result.applied} change${result.applied === 1 ? '' : 's'}.`
        : 'Already up to date.')
    } catch (e) {
      toast(errorText(e), { tone: 'warning' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="h2">Google Calendar</h2>
        <span className="spacer" />
        {status.connected ? <span className="pill">Connected</span> : null}
      </div>
      <div className="card-body" style={{ paddingTop: 0 }}>
        {status.connected ? (
          <>
            <p style={{ marginTop: 0, fontSize: 13 }}>
              Syncing with <strong>{status.email}</strong>. Last checked {when(status.last_synced_at)}.
            </p>
            {status.last_error ? (
              <p className="quiet" style={{ fontSize: 12.5 }}>
                <Icon name="warning" size={14} style={{ verticalAlign: -2 }} /> Last sync had a
                problem: {status.last_error}
              </p>
            ) : null}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button className="btn" onClick={syncNow} disabled={busy}>
                {busy ? 'Working…' : 'Sync now'}
              </button>
              <button className="btn" onClick={disconnect} disabled={busy}>Disconnect</button>
            </div>
          </>
        ) : (
          <>
            <p style={{ marginTop: 0, fontSize: 13 }}>
              Book an appointment here and it appears on your Google Calendar. Move or cancel it
              in Google and it changes here too.
            </p>
            <ul className="quiet" style={{ fontSize: 12.5, paddingLeft: 18, margin: '10px 0' }}>
              <li>Events already on your calendar are brought in, 30 days back and 6 months ahead.</li>
              <li>If the same meeting is edited on both sides, the most recent edit wins.</li>
              <li>Only your calendar’s events are read — not your mail, contacts or anything else.</li>
            </ul>
            <button className="btn primary" onClick={connect} disabled={busy}>
              {busy ? 'Opening Google…' : 'Connect Google Calendar'}
            </button>
          </>
        )}
      </div>
    </section>
  )
}
